import { useSetAtom } from "jotai";
import { useCallback } from "react";

import { ProceedToVerifyCodeStepAtom } from "../atoms/actions";
import { UserContactStatus } from "./__generated__/useCheckUserEmailStatusProfileMutation.graphql";
import { useCheckUserEmailStatusProfile } from "./useCheckUserEmailStatusProfile";
import { useCheckUserPhoneStatusProfile } from "./useCheckUserPhoneStatusProfile";
import { useCreateEmailVerificationProfile } from "./useCreateEmailVerificationProfile";
import { useCreatePhoneVerificationProfile } from "./useCreatePhoneVerificationProfile";

export const useCheckEmailorPhoneProfile = () => {
  const goToCodeVerification = useSetAtom(ProceedToVerifyCodeStepAtom);
  const [checkEmail, isCheckingEmail] = useCheckUserEmailStatusProfile();
  const [checkPhone, isCheckingPhone] = useCheckUserPhoneStatusProfile();
  const [createPhoneVerification, isCreatingPhoneVerification] =
    useCreatePhoneVerificationProfile();
  const [createEmailVerification, isCreatingEmailVerification] =
    useCreateEmailVerificationProfile();

  const isChecking =
    isCheckingEmail ||
    isCheckingPhone ||
    isCreatingPhoneVerification ||
    isCreatingEmailVerification;

  const handleSuccessResponse = useCallback(
    (value: string, response: UserContactStatus, type: "Email" | "Phone") => {
      const email = type === "Email" ? value : null;
      const phone = type === "Phone" ? value : null;

      // If not taken, undefined, or verified, we can proceed to the code verification step.
      if (
        response === "NOT_TAKEN" ||
        response === "UNVERIFIED" ||
        response === "VERIFIED"
      ) {
        if (!!email) {
          createEmailVerification(email, () => {
            goToCodeVerification({ email, phone });
          });
        } else if (!!phone) {
          createPhoneVerification(phone, () => {
            goToCodeVerification({ email, phone });
          });
        }
      }
    },
    [createEmailVerification, createPhoneVerification, goToCodeVerification]
  );

  const checkEmailOrPhoneProfile = ({
    value,
    type,
  }: {
    value: string;
    type: "Email" | "Phone";
  }) => {
    if (type === "Email") {
      checkEmail(value, handleSuccessResponse);
    } else {
      checkPhone(value, handleSuccessResponse);
    }
  };

  return [checkEmailOrPhoneProfile, isChecking] as const;
};
